import axios from 'axios';
import { athenaBaseUrl, GET, polusBaseUrl, POST } from '../../../utils/axios-interceptor';

// API Endpoints for Ask Aica feature - make sure there's no trailing slash
const API_ENDPOINTS = {
    CHAT: '/chat',
    CHAT_STOP: '/chat/stop',
    CHAT_STREAM: '/chat/stream',
    CHAT_HISTORY: '/chat/conversation/history',
    CONVERSATIONS: '/chat/conversations',
    CLEAR_MEMORY: '/chat/memory/clear',
    GET_ASKAICA_FILES: '/unifier/getAskAicaFiles',
};

// Athena emits server-relative download paths under this prefix; athenaBaseUrl
// already ends in "/athena", so we strip it before rebuilding the request.
const ATHENA_PATH_PREFIX = '/athena';

/**
 * Resolve an auth/identity value the same way the global axios interceptor does
 * (see applyInterceptor in utils/axios-interceptor): sessionStorage first —
 * where a normal session stores it — then localStorage as a fallback. The old
 * localStorage-only read returned an empty token in sessionStorage sessions,
 * which made authenticated calls such as file downloads 401.
 */
function getStoredValue(key: string): string {
    if (typeof window === 'undefined') return '';
    return window.sessionStorage?.getItem(key) ?? window.localStorage?.getItem(key) ?? '';
}

/**
 * Get the necessary headers for API requests
 * @returns Headers object with authentication tokens
 */
function getAuthHeaders(orgId?: string, userId?: string) {
    return {
        'Content-Type': 'application/json',
        'x-cerberus-token': getStoredValue('auth_token'),
        'x-user-id': userId ?? getStoredValue('user_id'),
        'x-organization-id': orgId ?? getStoredValue('org_id'),
    };
}

/**
 * Download a generated file (e.g. a one-pager DOCX) from Athena as a Blob.
 *
 * `fileUrl` is a server-relative path emitted by Athena
 * (e.g. "/athena/chat/download/<id>?filename=..."). We require the "/athena"
 * prefix and rebuild the request against athenaBaseUrl so the auth token is only
 * ever sent to the Athena origin — never to an attacker-supplied absolute URL.
 * Auth is resolved via getAuthHeaders (sessionStorage → localStorage), matching
 * the rest of the app.
 *
 * `orgId` must be the SAME context org the chat ran under (the portfolio/company
 * org from the route, not the viewer's own org). Athena owns the generated file
 * under that org and gates downloads on x-organization-id, so an investor viewing
 * a company must present the company org here — otherwise the request is a 403.
 */
export function downloadAskAicaFileService(fileUrl: string, orgId?: string) {
    if (!fileUrl.startsWith(`${ATHENA_PATH_PREFIX}/`)) {
        throw new Error('Invalid file URL');
    }
    const relativePath = fileUrl.slice(ATHENA_PATH_PREFIX.length);
    return axios({
        method: GET,
        url: `${athenaBaseUrl}${relativePath}`,
        headers: getAuthHeaders(orgId),
        responseType: 'blob',
    });
}

/**
 * Send a non-streaming chat request
 */
export function sendChatRequest(payload: {
    messages: Array<{ role: string; content: string }>;
    userId?: string;
    orgId?: string;
    sources?: string[];
}) {
    return axios({
        method: POST,
        url: `${CHAT_CONFIG.URL}`,
        headers: getAuthHeaders(payload.orgId, payload.userId),
        data: {
            messages: payload.messages,
            stream: false,
            user_id: payload.userId,
            org_id: payload.orgId,
            sources: payload.sources || ['searchTheWeb'],
        },
    });
}

export function getChatHistoryService(payload: {
    userId?: string;
    orgId?: string;
    pageNum: number;
    pageSize: number;
    conversationId?: string;
}) {
    return axios({
        method: GET,
        url: `${athenaBaseUrl}${API_ENDPOINTS.CHAT_HISTORY}`,
        headers: getAuthHeaders(payload.orgId, payload.userId),
        params: {
            user_id: payload.userId,
            org_id: payload.orgId,
            page: payload.pageNum,
            page_size: payload.pageSize,
            ...(payload.conversationId ? { conversation_id: payload.conversationId } : {}),
        },
    });
}

export function getOrgConversationsService(payload: { userId?: string; orgId?: string }) {
    return axios({
        method: GET,
        url: `${athenaBaseUrl}${API_ENDPOINTS.CONVERSATIONS}`,
        headers: getAuthHeaders(payload.orgId, payload.userId),
    });
}

/** `orgId` is optional — the portfolio (org-less) surface omits it once a
 * conversation_id is present, per the Athena contract (F-8, coordinated with
 * Athena H-10 making org_id optional on this endpoint). Leads always passes an
 * explicit orgId, so its request body is unchanged. */
export function clearMemoryService(payload: { userId: string; orgId?: string; conversationId?: string }) {
    return axios({
        method: POST,
        url: `${athenaBaseUrl}${API_ENDPOINTS.CLEAR_MEMORY}`,
        headers: getAuthHeaders(payload.orgId, payload.userId),
        data: {
            user_id: payload.userId,
            ...(payload.orgId ? { org_id: payload.orgId } : {}),
            ...(payload.conversationId ? { conversation_id: payload.conversationId } : {}),
        },
    });
}

/**
 * Fetch the Library file list for an org — via Polus (BEFE), not Athena.
 * Mirrors getLookalikesService: the global axios interceptor injects cerberus
 * auth headers for polus-routed calls, so no manual headers here.
 */
export function getAskAicaFilesService(orgId: string) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_ASKAICA_FILES,
        method: GET,
        params: { orgId },
    });
}

/**
 * Dedicated instance for the stop POST, for the same reason globalAskAica has one
 * (see its service.ts): the app-wide axios default carries an `x-organization-id`
 * DEFAULT header stamped by addTokenToHeaders, and axios merges per-request headers
 * over defaults rather than replacing them — so the portfolio surface's deliberately
 * org-less headers would still ship a stale org id.
 */
const askAicaStopAxios = axios.create();
delete askAicaStopAxios.defaults.headers['x-organization-id'];

/**
 * Stop an in-flight run. `headers` is passed in rather than built here because
 * the two surfaces differ on exactly one header: the portfolio (org-less) surface
 * must never send `x-organization-id`.
 */
export function stopAskAicaRunService(userMessageId: string, headers: Record<string, string>) {
    return askAicaStopAxios({
        method: POST,
        url: `${athenaBaseUrl}${API_ENDPOINTS.CHAT_STOP}`,
        headers,
        data: { user_message_id: userMessageId },
    });
}

/** Re-attach SSE URL. `cursor` is the last `id:` frame received — exclusive start;
 * omitted on a first attach, which asks for the full catch-up. */
export function getReattachStreamUrl(userMessageId: string, cursor?: string): string {
    const url = `${athenaBaseUrl}${API_ENDPOINTS.CHAT_STREAM}/${encodeURIComponent(userMessageId)}`;
    return cursor ? `${url}?cursor=${encodeURIComponent(cursor)}` : url;
}

/**
 * Chat API configuration
 */
export const CHAT_CONFIG = {
    URL: `${athenaBaseUrl}${API_ENDPOINTS.CHAT}`,
    getHeaders: getAuthHeaders,
};
